import React, { RefObject, useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Rootstate } from '../../store';
import { GRID_SIZE } from './Grid';

type coord = {
    x: number,
    y: number,
}

const MeasureTool = ({ gridRef }: { gridRef: RefObject<HTMLDivElement> }) => {
    const [measureEnd, setMeasureEnd] = useState<coord | undefined>();
    const { measuring, zoom, measureStart } = useSelector((state: Rootstate) => ({
        mode: state.map.mode,
        measuring: state.measure.measuring,
        measureStart: state.measure.measureStart,
        zoom: state.zoom.zoom,
    }), shallowEqual);

    useEffect(() => {
        if(measuring) {
            setMeasureEnd({ x: measureStart?.x ?? 0, y: measureStart?.y ?? 0 });
            const onMouseMove = (e: MouseEvent) => {
                const { x = 0, y = 0 } = gridRef.current?.getBoundingClientRect() ?? {};
                setMeasureEnd({ x: (e.clientX - x) / zoom, y: (e.clientY - y) / zoom });
            };
            const ref = gridRef.current;
            ref?.addEventListener('mousemove', onMouseMove);

            return () => {
                setMeasureEnd(undefined);
                ref?.removeEventListener('mousemove', onMouseMove);
            };
        }
    }, [measuring, measureStart, zoom]);

    if(!measuring || !measureStart || !measureEnd) {
        return null;
    }

    const startX = Math.min(measureStart.x, measureEnd.x);
    const startY = Math.min(measureStart.y, measureEnd.y);
    const endX = Math.max(measureStart.x, measureEnd.x);
    const endY = Math.max(measureStart.y, measureEnd.y);

    const pointStartX = startX === measureStart.x
        ? 0
        : endX - startX;
    const pointStartY = startY === measureStart.y
        ? 0
        : endY - startY;

    const pointEndX = startX === measureStart.x
        ? endX - startX
        : 0;
    const pointEndY = startY === measureStart.y
        ? endY - startY
        : 0;

    const xDistance = Math.round((endX - startX) * 100) / 100;
    const yDistance = Math.round((endY - startY) * 100) / 100;
    const length = Math.round(Math.sqrt(xDistance * xDistance + yDistance * yDistance) * 10) / 10;

    return (
        <>
            <svg className='absolute z-20' style={{
                left: `${startX - 2.5}px`,
                top: `${startY - 2.5}px`,
                width: `${Math.max(endX - startX, 5)}px`,
                height: `${Math.max(endY - startY, 5)}px`,
            }} >
                <polygon
                    className='stroke-[4px] stroke-slate-950'
                    points={`${pointStartX}, ${pointStartY} ${pointEndX}, ${pointEndY}`}
                />
            </svg>
            <div className='absolute bg-slate-100 rounded-xl p-1 text-slate-950 text-xs z-20' style={{
                left: `${(measureStart.x)}px`,
                top: `${(measureStart.y)}px`,
                scale: `${1/zoom}`,
            }}>
                {Math.round((length / GRID_SIZE * 5) * 10) / 10} feet
            </div>
        </>
    );
};

export default MeasureTool;
